import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Banner } from '@parchment/atoms';
import { AddressIcon, InvalidAddressIcon } from '@parchment/icons';
import { addressToString, validateAddress as validateAddressViaApi, autocompleteAddress as autocompleteAddressViaApi } from '../../utils/address';
import debounce from 'debounce-promise';
import { AnchorList } from './AnchorList';
export const AUTOCOMPLETE_IN_USE = false;
const noop = () => undefined;
const validateAddressViaApiWithDebounce = debounce(validateAddressViaApi, 300);
export default function AddressVerification(_ref) {
  let {
    autoSelectVerified = false,
    verify = true,
    hideValidAddressBanner = false,
    autocomplete = false,
    address = {},
    onSelect = noop,
    verifyAddress = validateAddressViaApiWithDebounce,
    autocompleteAddress = autocompleteAddressViaApi,
    root = '/'
  } = _ref;
  const [verifying, setVerifying] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  useEffect(() => {
    (async () => {
      const {
        verified,
        address1,
        city,
        state,
        country
      } = address;
      // we will assume its US if the country is falsy or is "US" (case insensitive)
      const assumeUS = !country || country.toUpperCase() === 'US';
      if (!address1 || !address1.trim() || !assumeUS || verified) {
        suggestions.length > 0 && setSuggestions([]);
        return;
      }
      if (verify && address1 && city && state) {
        setVerifying(true);
        try {
          const candidates = (await verifyAddress(address, root)).filter(candidate => candidate.address1 !== address.address1 || candidate.address2 !== address.address2 || candidate.city !== address.city || candidate.state !== address.state || candidate.zip !== address.zip || candidate.country !== address.country);
          if (candidates && candidates.length === 1 && candidates[0].verified && autoSelectVerified) {
            onSelect({
              ...address,
              ...candidates[0]
            });
            suggestions.length > 0 && setSuggestions([]);
          } else {
            setSuggestions(candidates);
          }
        } finally {
          setVerifying(false);
        }
      } else if (autocomplete && AUTOCOMPLETE_IN_USE) {
        setSuggestions((await autocompleteAddress(address, root)).filter(suggestion => suggestion.address1 !== address.address1 || suggestion.city !== address.city || suggestion.state !== address.state).map(suggestion => ({
          ...address,
          ...suggestion
        })));
      }
    })();
  }, [address, autoSelectVerified, autocomplete, verify]);
  const {
    address1,
    city,
    state,
    verified = false
  } = address;
  let banner = null;
  if (verify && verified && !hideValidAddressBanner) {
    banner = /*#__PURE__*/React.createElement(StyledBanner, {
      context: "dark",
      icon: /*#__PURE__*/React.createElement(AddressIcon, null),
      message: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Title, null, "Valid address:"), " This address has been validated. Please continue.")
    });
  } else if (verify && address1 && city && state && !verifying && !verified) {
    const hasSuggestions = suggestions && suggestions.length > 0;
    banner = /*#__PURE__*/React.createElement(StyledBanner, {
      context: "warning",
      icon: /*#__PURE__*/React.createElement(InvalidAddressIcon, null),
      message: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Title, null, "Unable to validate address", hasSuggestions && ':'), " ", hasSuggestions && 'Here are some suggestions')
    });
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, banner, !verifying && (suggestions === null || suggestions === void 0 ? void 0 : suggestions.length) > 0 && /*#__PURE__*/React.createElement(Suggestions, {
    onItemClick: index => {
      const selected = suggestions[index];
      onSelect({
        verified: false,
        ...selected
      });
      setSuggestions([]);
    },
    items: suggestions.map(addressToString)
  }));
}
const StyledBanner = /*#__PURE__*/styled(Banner).withConfig({
  displayName: "AddressVerification__StyledBanner",
  componentId: "dw3phg-0"
})(["margin-top:20px;"]);
const Title = /*#__PURE__*/styled.span.withConfig({
  displayName: "AddressVerification__Title",
  componentId: "dw3phg-1"
})(["text-transform:uppercase;"]);
const Suggestions = /*#__PURE__*/styled(AnchorList).withConfig({
  displayName: "AddressVerification__Suggestions",
  componentId: "dw3phg-2"
})(["margin-top:20px;"]);